// libraries
import {
  FieldLabel,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectEmailSendingFromState,
  selectEmailSentFromState,
} from 'src/app/ResetPassword/redux/resetPassword.selectors'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { postCognitoResetPasswordInit } from 'src/redux/actions/auth.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface CodeVerificationProps {
  value?: string
  onChange: (val: string) => void
  errors?: Array<React.ReactElement | string>
  email: string
}

const CodeVerification: React.FC<CodeVerificationProps> = (
  props,
): React.ReactElement => {
  const dispatch = useBackgroundDispatch()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const resetPasswordEmailSent = useSelector(selectEmailSentFromState)
  const resetPasswordEmailSending = useSelector(selectEmailSendingFromState)

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)

  const cannotSendNewCode = resetPasswordEmailSent ? (
    <Trns source="We cannot send you a new code at this time. Please try again later." />
  ) : undefined

  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setShowErrors(false)
    props.onChange(e.target.value)
  }

  const sendNewCode = (): void => {
    if (!resetPasswordEmailSent) {
      dispatch(
        postCognitoResetPasswordInit(
          {
            email: props.email,
          },
          true,
        ),
      )
      setShowErrors(false)
    } else {
      setShowErrors(true)
    }
  }

  return (
    <>
      <div
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 24px;
        `}
      >
        <Trns
          source={'Enter the 6-digit verification code we sent to ${email}'}
          expressions={{
            email: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {props.email}
              </span>
            ),
          }}
        />
      </div>
      <div
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 40px;
        `}
      >
        <Trns
          source={
            "Choose a strong password and don't reuse it for other accounts."
          }
        />
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: flex-end;
        `}
      >
        <FieldLabel
          css={css`
            flex-grow: 1;
          `}
        >
          {<Trns source={'Code'} />}
        </FieldLabel>
        <HoverableLink
          onClick={sendNewCode}
          css={css`
            align-self: center;
            margin-bottom: 6px;
          `}
          ariaLabelSource={'Send a new code'}
          disabled={resetPasswordEmailSending}
        >
          <Trns source={'Send a new code'} />
        </HoverableLink>
      </div>
      <input
        css={css`
          width: 100%;
          border-radius: 6px;
          height: 50px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          padding: 20px;
          caret-color: ${themeConstants.colors.cta};
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          background-color: ${styleConstants.colors.gray_lightest};

          &::placeholder {
            opacity: 0.5;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          }

          &:focus,
          &:hover {
            background-color: ${styleConstants.colors.white};
            border: 1px solid ${themeConstants.colors.cta};
          }

          ${(props.errors || cannotSendNewCode) &&
          showErrors &&
          css`
            border: 1px solid ${styleConstants.colors.red};

            &:focus,
            &:hover {
              border: 1px solid ${styleConstants.colors.red};
            }
          `}
        `}
        placeholder={translateSingleString(locale, 'Enter 6-digit code')}
        value={props.value}
        type={'text'}
        onChange={onChange}
      />
      {(props.errors || cannotSendNewCode) && showErrors && (
        <div
          css={css`
            margin-top: 4px;
            margin-left: 20px;
            ${getFontCss(
              12,
              styleConstants.colors.red,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {props.errors || cannotSendNewCode}
        </div>
      )}
    </>
  )
}

export default CodeVerification
